import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Document {
    webkitFullscreenEnabled: boolean;
    webkitFullscreenElement: Element | null;
    webkitCancelFullScreen: () => Promise<void>;
  }

  interface HTMLElement {
    webkitRequestFullscreen?: () => void;
  }
}

function useFullscreen() {
  /* フルスクリーンモードが使用可能かどうか */
  const [enabled, setEnabled] = useState(false);
  /* 現在ブラウザがフルスクリーンモードかどうか */
  const [active, setActive] = useState(false);

  /* フルスクリーンモードが使用可能かどうかを監視 */
  useEffect(() => {
    setEnabled(
      !!document.fullscreenEnabled || !!document.webkitFullscreenEnabled,
    );
  }, []);

  /**
   * フルスクリーンモードの変更時の処理。
   */
  const handleFullscreenChange = useCallback(() => {
    setActive(
      !!document.fullscreenElement || !!document.webkitFullscreenElement,
    );
  }, []);

  /* フルスクリーンモードの変更を監視 */
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    handleFullscreenChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
    };
  }, [handleFullscreenChange]);

  const requestFullscreen = useCallback((elem: HTMLElement) => {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    }
  }, []);

  const toggleFullscreen = useCallback(
    (elem: HTMLElement) => {
      active ? exitFullscreen() : requestFullscreen(elem);
    },
    [active, exitFullscreen, requestFullscreen],
  );

  const addFullscreenChangeListener = useCallback((listener: () => void) => {
    document.addEventListener('fullscreenchange', listener);
    document.addEventListener('webkitfullscreenchange', listener);
  }, []);

  const removeFullscreenChangeListener = useCallback((listener: () => void) => {
    document.removeEventListener('fullscreenchange', listener);
    document.removeEventListener('webkitfullscreenchange', listener);
  }, []);

  return {
    isFullscreenEnabled: enabled,
    isFullscreenActive: active,
    requestFullscreen,
    exitFullscreen,
    toggleFullscreen,
    addFullscreenChangeListener,
    removeFullscreenChangeListener,
  };
}

export default useFullscreen;
